import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Col, Select } from 'antd';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import FormItemInputComponent from './FormItemInputComponent';

// Services
import { getUsers } from '../../Network/Core/Users/UserServices';
import {
  getProjectUsers,
  updateProject,
} from '../../Network/Core/Project/ProjectServices';

// Images
import images from '../../Assets/Images';
import { getErrorMessage } from '../../Utils/errorHelpers';

interface FormValues {
  name: string;
  assignedUsers: string[];
}

interface EditProjectFormComponentProps {
  project: any;
  FormTitle?: string;  // ← now declared here
}

const EditProjectFormComponent: React.FC<EditProjectFormComponentProps> = ({
                                                                             project,
                                                                             FormTitle,
                                                                           }) => {
  const [form] = Form.useForm<FormValues>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [users, setUsers] = useState<{ label: string; value: string }[]>([]);
  const [fetched, setFetched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Re‑validate on every change
  const watched = Form.useWatch([], form);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsFormValid(true), () => setIsFormValid(false));
  }, [watched, form]);

  // On mount, load users and assigned users
  useEffect(() => {
    (async () => {
      await loadUsers('');
      await loadAssignedUsers();
    })();
  }, []);

  const loadUsers = async (search: string = '') => {
    try {
      const { data } = await getUsers(search);
      setUsers(
        data.users.map((u: any) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u.email,
        }))
      );
    } catch {
      toast.error('Failed to load users');
    }
  };

  const loadAssignedUsers = async () => {
    try {
      const { data } = await getProjectUsers(id!);
      form.setFieldsValue({
        name: project.name,
        assignedUsers: data.users,
      });
      setFetched(true);
    } catch {
      toast.error('Failed to load assigned users');
    }
  };

  const onFinish = async (values: FormValues) => {
    try {
      await updateProject({
        id: id!,
        updatedProject: values,
      });
      toast.success('Project updated successfully');
      navigate(-1);
    } catch (err: any) {
      // Per product decision: never surface a 403 to the user. Stay on the form
      // silently (no toast, no navigate) rather than showing "Access denied.".
      // Still log to console for debugging. console.log isn't captured by Sentry's
      // captureConsole (error/warn only), so this adds no duplicate event (the axios
      // interceptor already reports the 403 to Sentry once) and stays visible by
      // default (unlike console.debug, which Chrome hides under "Verbose").
      if (err?.response?.status === 403) {
        console.log('[EditProject] 403 suppressed:', err);
        return;
      }
      toast.error(getErrorMessage(err, 'Failed to update project'));
    }
  };

  return (
    <Form
      form={form}
      name={FormTitle ?? 'edit-project-form'}     // ← uses FormTitle if provided
      className="common-form"
      layout="vertical"
      initialValues={{ name: project.name }}
      onFinish={onFinish}
    >
      <Row gutter={{ xs: 8, sm: 16, md: 20, lg: 30 }}>
        <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }}>
          <FormItemInputComponent
            label="Project Name"
            name="name"
            className="name"
            placeholder="Enter Project Name"
            inputType="text"
            suffix={<img src={images.LegalName} alt="name" />}
            rules={[
              { required: true, message: 'Project Name is required' },
              { max: 30, message: 'Project Name must be max 30 characters.' },
            ]}
          />
        </Col>

        <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item
            label="Assign User"
            name="assignedUsers"
            rules={[{ required: true, message: 'Pick at least one user' }]}
            className="assign_project"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              onSearch={loadUsers}
              filterOption={false}
              style={{ width: '100%' }}
              placeholder="Assign Users to Project"
              options={users}
              disabled={!fetched}
              loading={!fetched}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <div className="bottom-btn-section">
          <Button
            className="btn-link"
            htmlType="reset"
            onClick={() => navigate('/projects-list')}
          >
            Cancel
          </Button>
          <Button
            className="common-btn"
            type="primary"
            htmlType="submit"
            disabled={!isFormValid}
          >
            Update Project
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default EditProjectFormComponent;